/**
 * helpers.js
 * ---------------------------------------------------------------------
 * Shared utility functions for MapPage components and map-related utils.
 *
 * Includes:
 *   • Colour generation and scaling functions
 *   • Apache Arrow table helpers
 *   • Label and colour mapping
 *   • ID normalisation
 * ---------------------------------------------------------------------
 */

import { LEVEL_LOCAL_AUTHORITY, levelIdKey, resolveLocalAuthorityCode } from "../geoLevels";

/* ---------------------------------------------------------------------
 * 1️⃣ TABLE UTILITIES
 * ------------------------------------------------------------------- */

/**
 * Converts an Apache Arrow Table into an array of plain JS objects.
 * @param {Table} table - Apache Arrow table from tableFromIPC()
 * @returns {Array<Object>} rows - List of row objects
 */
export const rowsFromTable = (table) => {
  if (!table) return [];
  const cols = table.schema.fields.map((f) => f.name);
  const rows = [];
  for (let i = 0; i < table.numRows; i++) {
    const obj = {};
    for (const c of cols) obj[c] = table.getChild(c).get(i);
    rows.push(obj);
  }
  return rows;
};

/* ---------------------------------------------------------------------
 * 2️⃣ ID NORMALISATION
 * ------------------------------------------------------------------- */

/**
 * Normalises area ID property keys from shapefile feature properties.
 *
 * For local authorities this also resolves the six councils whose code the
 * boundary file and the data disagree on, so the returned ID is always the one
 * the backend stores as `area_cd`. See resolveLocalAuthorityCode.
 *
 * @param {"constituency"|"ward"|"local_authority"} level
 * @param {Object} props - Feature properties
 * @returns {string|null} Normalised ID string
 */
export const normalizeId = (level, props) => {
  const key = levelIdKey(level);
  const v =
    props?.[key] ?? props?.[key.toUpperCase()] ?? props?.[key.toLowerCase()];
  if (!v) return null;
  const id = String(v);
  return level === LEVEL_LOCAL_AUTHORITY ? resolveLocalAuthorityCode(id) : id;
};

// Parses PCON24CD from the new ward file format (JSON array string) or old format (plain string).
// Always returns a string array.
export const parsePCON24CDs = (val) => {
  if (!val) return [];
  try {
    const parsed = JSON.parse(String(val));
    if (Array.isArray(parsed)) return parsed.map(String);
    if (parsed) return [String(parsed)];
  } catch {}
  return [String(val)];
};

/* ---------------------------------------------------------------------
 * 3️⃣ COLOUR UTILITIES
 * ------------------------------------------------------------------- */

/**
 * Clamps a numeric value between 0 and 1.
 */
export const clamp01 = (x) => Math.max(0, Math.min(1, x));

/**
 * Converts a HEX colour string to HSL.
 * @param {string} hex - Colour in #RRGGBB format
 * @returns {{h:number, s:number, l:number}}
 */
export const hexToHsl = (hex) => {
  let c = hex.replace("#", "");
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Converts HSL values back to a HEX colour.
 */
export const hslToHex = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

  return (
    "#" +
    [r, g, b]
      .map((v) => v.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()
  );
};

/**
 * Generates a single-ended (0 → base colour) heat scale.
 * @param {string} baseHex - Base HEX colour
 * @param {Array<number>} values - Data values for scaling
 * @returns {(v:number)=>string} Colour function
 */
export const makeHeatScale = (baseHex, values) => {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : 0;
  const max = finite.length ? Math.max(...finite) : 100;
  const { h, s } = hexToHsl(baseHex);

  return (v) => {
    if (!Number.isFinite(v)) return "#CCCCCC";
    const t = clamp01((v - min) / Math.max(1e-6, max - min));
    const light = 95 - t * 60;
    return hslToHex(h, s, light);
  };
};

/**
 * Generates a two-point (negative→positive) heat scale.
 * @param {string} baseHexPos - Base colour for positive values
 * @param {string} baseHexNeg - Base colour for negative values
 * @param {Array<number>} values - Data values
 * @returns {(v:number)=>string} Colour function
 */
export const make2pointHeatScale = (baseHexPos, baseHexNeg, values) => {
  const finite = values.filter((v) => Number.isFinite(v));
  const min = finite.length ? Math.min(...finite) : -100;
  const max = finite.length ? Math.max(...finite) : 100;

  return (v) => {
    if (!Number.isFinite(v)) return "#CCCCCC";
    const t = v >= 0 ? v / Math.max(1e-6, max) : v / Math.min(-1e-6, min);
    const { h, s } = hexToHsl(v >= 0 ? baseHexPos : baseHexNeg);
    const light = 95 - clamp01(Math.abs(t)) * 60;
    return hslToHex(h, s, light);
  };
};

/* ---------------------------------------------------------------------
 * 4️⃣ GROUP & LABEL UTILITIES
 * ------------------------------------------------------------------- */

/**
 * Determines base colour for Likert-type responses.
 */
export const baseColorForLikertKey = (group, key) => {
  const pol = group.polarity || {};

  const tag = Object.entries(pol).find(([_, value]) => {
    if (Array.isArray(value)) return value.includes(key);
    return value === key;
  })?.[0];

  if (tag === "pos") return "#2E7D32"; // green
  if (tag === "neg") return "#C62828"; // red
  return "#6E6E6E"; // neutral grey
};

/**
 * Builds label and colour dictionaries for each key in group data.
 * @param {Object} groupsById - Group definitions from props
 * @param {string[]} theme_colours - Colour palette
 * @returns {{labels:Object, colors:Object}}
 */
export const buildLabelsAndColors = (groupsById, theme_colours) => {
  const labels = {};
  const colors = {};

  Object.values(groupsById).forEach((g) => {
    // likert_grid groups have no direct keys — sub-groups handle their own colours
    if (g.type === "likert_grid") return;
    let i = 0;
    g.keys.forEach((k) => {
      // --- Labels ---
      labels[k] = g.full_labels?.[k] || g.labels?.[k] || k;

      // --- Colours ---
      if (!colors[k]) {
        if (g.colors?.[k]) {
          colors[k] = g.colors[k];
        } else if (g.type === "likert") {
          colors[k] = baseColorForLikertKey(g, k);
        } else {
          colors[k] = theme_colours[i % theme_colours.length];
          i += 1;
        }
      }
    });
  });

  return { labels, colors };
};
